// The shared ranking, when there is one.
//
// The game is completely playable with no server: this module only ever adds a
// board on top of the local one. Every call fails soft, and a dead or missing
// endpoint leaves the game working and says so in the panel — because a game
// that needs the network before it will start is a worse game.

import { LEADERBOARD_URL, LEADERBOARD_TIMEOUT_MS } from './config.js';

/** What the panel prints: 'online', 'offline', or why it could not reach it. */
let status = 'noserver';
/** True once a call has actually succeeded, so the panel can tell the two apart. */
let everOnline = false;

export function onlineStatus() {
  return status;
}

export function isOnline() {
  return everOnline;
}

/**
 * One request, with a deadline. `fetch` has no timeout of its own, so without
 * the AbortController a server that accepts the connection and then says nothing
 * would leave the panel waiting forever.
 */
async function request(url, options = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LEADERBOARD_TIMEOUT_MS);

  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    const data = await res.json().catch(() => null);

    if (!res.ok) throw new Error((data && data.error) || `server said ${res.status}`);
    return data;
  } finally {
    clearTimeout(timer);
  }
}

function failed(err) {
  everOnline = false;
  status = err && err.name === 'AbortError'
    ? 'timeout'
    : 'noserver';
  return { ok: false, error: status };
}

/** The top of the board. Never throws. */
export async function loadTop(limit = 10) {
  try {
    const data = await request(`${LEADERBOARD_URL}?limit=${encodeURIComponent(limit)}`);
    const entries = data && Array.isArray(data.entries) ? data.entries : [];

    everOnline = true;
    status = 'online';
    return { ok: true, entries };
  } catch (err) {
    return failed(err);
  }
}

/** Posts a finished match. Fire and forget at every call site. */
export async function publish(entry) {
  try {
    const data = await request(LEADERBOARD_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(entry),
    });

    everOnline = true;
    status = 'online';
    return { ok: true, rank: data && data.rank };
  } catch (err) {
    return failed(err);
  }
}
